import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { can } from "@/lib/constants";
import { Spinner } from "@/components/common";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import { ChangePasswordPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordPages";
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import LeadFormPage from "@/pages/LeadFormPage";
import PropertiesPage from "@/pages/PropertiesPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import PropertyFormPage from "@/pages/PropertyFormPage";
import UsersPage from "@/pages/UsersPage";
import SettingsPage from "@/pages/SettingsPage";
import MorePage from "@/pages/MorePage";
import ActivityPage from "@/pages/ActivityPage";
import ChatPage from "@/pages/ChatPage";
import TasksPage from "@/pages/TasksPage";
import PlansPage from "@/pages/PlansPage";
import MatchesPage from "@/pages/MatchesPage";
import ExpensesPage from "@/pages/ExpensesPage";
import DealsPage from "@/pages/DealsPage";
import { RealtimeProvider } from "@/context/RealtimeContext";

function RequireAuth({ children, allowPwChange }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Spinner className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (user.must_change_password && !allowPwChange) return <Navigate to="/change-password" replace />;
  return children;
}

function RequireModule({ m, children }) {
  const { user } = useAuth();
  if (!can(user, m)) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner className="min-h-screen" />;
  if (user) return <Navigate to={user.must_change_password ? "/change-password" : "/dashboard"} replace />;
  return children;
}

const guard = (m, el) => <RequireModule m={m}>{el}</RequireModule>;

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/change-password" element={<RequireAuth allowPwChange><ChangePasswordPage /></RequireAuth>} />
      <Route element={<RequireAuth><RealtimeProvider><AppShell /></RealtimeProvider></RequireAuth>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/chat" element={guard("chat", <ChatPage />)} />
        <Route path="/tasks" element={guard("tasks", <TasksPage />)} />
        <Route path="/plans" element={guard("chat", <PlansPage />)} />
        <Route path="/matches" element={guard("leads", <MatchesPage />)} />
        <Route path="/expenses" element={guard("finance", <ExpensesPage />)} />
        <Route path="/deals" element={guard("finance", <DealsPage />)} />
        <Route path="/leads" element={guard("leads", <LeadsPage />)} />
        <Route path="/leads/new" element={guard("leads", <LeadFormPage />)} />
        <Route path="/leads/:id" element={guard("leads", <LeadDetailPage />)} />
        <Route path="/leads/:id/edit" element={guard("leads", <LeadFormPage />)} />
        <Route path="/properties" element={guard("properties", <PropertiesPage />)} />
        <Route path="/properties/new" element={guard("properties", <PropertyFormPage />)} />
        <Route path="/properties/:id" element={guard("properties", <PropertyDetailPage />)} />
        <Route path="/properties/:id/edit" element={guard("properties", <PropertyFormPage />)} />
        <Route path="/users" element={guard("users", <UsersPage />)} />
        <Route path="/activity" element={guard("activity", <ActivityPage />)} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/more" element={<MorePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}
